import React, { useEffect } from "react"
import {Button, ButtonProps, Text} from "../../vanilla"
import {Nullable} from "../../types/utils"
import {Hit} from "../../store/ProductStore"
import {getDisabledButtonText} from "../../utils/utils"
import {useBasketStore, useCustomerStore} from '../../store/hooks/useStore'
import {useLocation} from 'react-router-dom'
import {redirectIfNotRegistered} from "../../utils/login-redirect-utils"
import useNavigation from '../../hooks/use-navigation'
import { useAddToCartTracking } from "../../analytics/click-event-tracking"

interface AddToBasketButtonProps extends ButtonProps {
    hit: Hit,
    displayPrice: Nullable<number>
    isPLPTile?: boolean
    isPDPPage?: boolean
    hitItemIndex?: number
    ATBEventDetails?: {
        locationOfCart?: string
        viewingCategoryId?: string
        searchQuery?: string
    }
    isSearch?: boolean
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
    navigate?: ReturnType<typeof useNavigation>
}

export const ATBButton = React.forwardRef((props: AddToBasketButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const {
        hit,
        displayPrice,
        isPLPTile,
        isPDPPage,
        hitItemIndex,
        isSearch = false,
        ATBEventDetails,
        onClick,
        navigate: navigationProp,
        ...rest
    } = props

    // Possible reasons ATB button would be disabled:
    // 1. global inStock flag is set to false (Handled in transformHits and part of availableforDayAtStore check)
    // 2. Product has no display price
    // 3. Product is not available for sale on the current slot/store combination
    // 4. Product is badged with the isAddToCartDisabled flag
    const isButtonDisabled = !displayPrice
                            || !hit.availableForDayAtStore
                            || hit.isAddToCartDisabled === true

    // Re-direct if customer is not logged in
    let navigate = useNavigation()
    navigate = navigationProp ? navigationProp : navigate
    const location = useLocation()
    const pathname = `${location.pathname}${location.search}${location.hash}`
    const {isRegistered, loading: loadingCustomer} = useCustomerStore()
    const basketStore = useBasketStore()
    const sendAddToCartData = useAddToCartTracking()
    const { locationOfCart, viewingCategoryId, searchQuery } = ATBEventDetails || {}
    const ATBPayload = {
        hit,
        cartLocation: locationOfCart || '',
        viewingCategoryId: viewingCategoryId || '',
        searchQuery: searchQuery || '',
        hitItemIndex
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!isRegistered && !loadingCustomer) {
            basketStore.setPendingATBEvent(ATBPayload)
            basketStore.setPendingProductId(hit.id)
        }
        if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer}) && onClick) {
            onClick(event)
        }
    }

    useEffect(() => {
        if (basketStore.pendingATBEvent) {
            const { hit, cartLocation, viewingCategoryId, searchQuery, hitItemIndex } = basketStore.pendingATBEvent
            sendAddToCartData
            (
                hit,
                cartLocation,
                viewingCategoryId,
                searchQuery,
                hitItemIndex
            )
            basketStore.setPendingATBEvent(null)
        }
    }, [basketStore.pendingATBEvent]
)

    return (
        <Button
        variant={!isButtonDisabled ? "primary" : "secondary"}
        disabled={isButtonDisabled}
        size={(isPDPPage || isPLPTile) ? "smWide" : "sm"}
        paddingY={(isPDPPage || isPLPTile) ? "8px" : "0px"}
        data-test-selector="add-to-basket-button"
        onClick={handleClick}
        {...rest}
        ref={ref}>
            {isSearch
                ? <Text variant="unstyled" fontSize="sm" display='block'>
                        {!isButtonDisabled ? 'Add' : getDisabledButtonText(hit.disabledStatus)}
                    </Text>
                :  <>
                    <Text variant="unstyled" fontSize={isPDPPage ? "sm" : "inherit"} display={isPLPTile ? ['none', 'block'] : 'block'}>{!isButtonDisabled ? 'Add to Basket' : getDisabledButtonText(hit.disabledStatus)}</Text>
                    <Text variant="unstyled" fontSize={isPLPTile ? "sm" : "inherit"} display={isPLPTile ? ['block', 'none'] : 'none'}>
                        {!isButtonDisabled ? 'Add' : getDisabledButtonText(hit.disabledStatus)}
                    </Text>
                </>
            }
        </Button>
    )
})
